"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import StoryViewer from "@/components/stories/StoryViewer";
import StoryRing from "@/components/stories/StoryRing";
import {
  appendUniqueStoryBusinesses,
  filterPublicStoryGroups,
  getStoryDiscoveryLocationKey,
  getStoryBusinessNewestAt,
  isStoryBusinessGroup,
  isStoryBusinessSeen,
  orderStoryBusinessesBySeen,
  type SeenStoryBusinessMap,
  type StoryBusinessGroup,
  type StoryBusinessTrayItem,
} from "@/lib/stories/businessStories";

const SEEN_BUSINESS_STORAGE_KEY = "yb:business-stories:seen-businesses:v1";

function readSeenBusinesses(): SeenStoryBusinessMap {
  if (typeof window === "undefined") return {};
  try {
    const value = JSON.parse(window.localStorage.getItem(SEEN_BUSINESS_STORAGE_KEY) || "{}");
    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
  } catch {
    return {};
  }
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "YB";
}

function activeTrayItems(groups: StoryBusinessTrayItem[], now: number) {
  const fullGroups = groups.filter(isStoryBusinessGroup);
  const usableFullGroups = new Map(
    filterPublicStoryGroups(fullGroups, now).map((group) => [group.businessId, group])
  );
  const active: StoryBusinessTrayItem[] = [];
  for (const item of groups) {
    if (!isStoryBusinessGroup(item)) active.push(item);
    else {
      const usable = usableFullGroups.get(item.businessId);
      if (usable) active.push(usable);
    }
  }
  return active;
}

function locationParams(location: any) {
  const params = new URLSearchParams();
  if (location?.city) params.set("city", String(location.city));
  if (location?.region || location?.state) params.set("state", String(location.region || location.state));
  if (location?.lat != null) params.set("lat", String(location.lat));
  if (location?.lng != null) params.set("lng", String(location.lng));
  return params;
}

type BusinessStoriesRowProps = {
  groups?: StoryBusinessTrayItem[];
  mode?: "public" | "customer";
  compact?: boolean;
  location?: any;
  nextCursor?: string | null;
  hasMore?: boolean;
  progressive?: boolean;
};

function sourceKey(
  groups: StoryBusinessTrayItem[],
  nextCursor: string | null,
  hasMore: boolean,
  location: any
) {
  const itemKey = groups.map((item) => isStoryBusinessGroup(item)
    ? `${item.businessId}:${item.stories.map((story) => `${story.id}:${story.expiresAt}`).join(",")}`
    : `${item.businessId}:${item.newestStoryAt}`
  ).join("|");
  const locationKey = getStoryDiscoveryLocationKey(location);
  return `${locationKey}::${itemKey}::${nextCursor || ""}::${hasMore}`;
}

export default function BusinessStoriesRow(props: BusinessStoriesRowProps) {
  const groups = props.groups || [];
  return (
    <BusinessStoriesRowStateful
      key={sourceKey(groups, props.nextCursor || null, Boolean(props.hasMore), props.location)}
      {...props}
      groups={groups}
    />
  );
}

function BusinessStoriesRowStateful({
  groups = [],
  mode = "public",
  compact = false,
  location = null,
  nextCursor = null,
  hasMore = false,
  progressive = false,
}: BusinessStoriesRowProps) {
  const [eligibilityTime, setEligibilityTime] = useState(() => Date.now());
  const [items, setItems] = useState<StoryBusinessTrayItem[]>(() => groups);
  const [moreAvailable, setMoreAvailable] = useState(hasMore);
  const [currentCursor, setCurrentCursor] = useState(nextCursor);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [paginationArmed, setPaginationArmed] = useState(false);
  const [seenBusinesses, setSeenBusinesses] = useState<SeenStoryBusinessMap>({});
  const [viewerGroups, setViewerGroups] = useState<StoryBusinessGroup[] | null>(null);
  const [openingBusinessId, setOpeningBusinessId] = useState<string | null>(null);
  const trayRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef(items);
  const cursorRef = useRef(nextCursor);
  const hasMoreRef = useRef(moreAvailable);
  const pageRequestRef = useRef<Promise<StoryBusinessTrayItem[]> | null>(null);
  const pageAbortRef = useRef<AbortController | null>(null);
  const viewerOrderRef = useRef<string[]>([]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setSeenBusinesses(readSeenBusinesses()));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => () => pageAbortRef.current?.abort(), []);

  const eligibleItems = useMemo(
    () => activeTrayItems(items, eligibilityTime),
    [eligibilityTime, items]
  );
  const orderedItems = useMemo(
    () => orderStoryBusinessesBySeen(eligibleItems, seenBusinesses),
    [eligibleItems, seenBusinesses]
  );

  useEffect(() => {
    const nextExpiry = items
      .filter(isStoryBusinessGroup)
      .flatMap((group) => group.stories)
      .map((story) => new Date(story.expiresAt).getTime())
      .filter((value) => Number.isFinite(value) && value > Date.now())
      .sort((left, right) => left - right)[0];
    if (!nextExpiry) return undefined;
    const timer = window.setTimeout(
      () => setEligibilityTime(Date.now()),
      Math.min(nextExpiry - Date.now() + 25, 2_147_483_647)
    );
    return () => window.clearTimeout(timer);
  }, [eligibilityTime, items]);

  const loadMore = useCallback(() => {
    if (!progressive || !hasMoreRef.current || !cursorRef.current) {
      return Promise.resolve(itemsRef.current);
    }
    if (pageRequestRef.current) return pageRequestRef.current;
    setLoadingMore(true);
    setLoadError(false);
    const controller = new AbortController();
    pageAbortRef.current = controller;
    const params = locationParams(location);
    params.set("cursor", cursorRef.current);
    params.set("limit", "16");
    const request = fetch(`/api/stories?${params.toString()}`, {
      cache: "no-store",
      signal: controller.signal,
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error("Story discovery page failed");
        const incoming = Array.isArray(payload?.items) ? payload.items : [];
        const merged = appendUniqueStoryBusinesses(itemsRef.current, incoming);
        itemsRef.current = merged;
        cursorRef.current = payload?.nextCursor || null;
        hasMoreRef.current = payload?.hasMore === true;
        setItems(merged);
        setCurrentCursor(cursorRef.current);
        setMoreAvailable(hasMoreRef.current);
        return merged;
      })
      .catch((error) => {
        if (error?.name !== "AbortError") setLoadError(true);
        return itemsRef.current;
      })
      .finally(() => {
        pageRequestRef.current = null;
        if (pageAbortRef.current === controller) pageAbortRef.current = null;
        setLoadingMore(false);
      });
    pageRequestRef.current = request;
    return request;
  }, [location, progressive]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = trayRef.current;
    if (!paginationArmed || !sentinel || !root || !moreAvailable || typeof IntersectionObserver === "undefined") {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void loadMore();
      },
      { root, rootMargin: "0px 160px 0px 0px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [currentCursor, loadMore, moreAvailable, paginationArmed]);

  const loadSequence = useCallback(async (item: StoryBusinessTrayItem) => {
    if (isStoryBusinessGroup(item)) {
      return filterPublicStoryGroups([item])[0] || null;
    }
    const params = new URLSearchParams({ businessId: item.businessId });
    const response = await fetch(`/api/stories?${params.toString()}`, { cache: "no-store" });
    if (!response.ok) return null;
    const payload = await response.json().catch(() => ({}));
    return payload?.storyGroup || null;
  }, []);

  const openStoryBusiness = useCallback(async (item: StoryBusinessTrayItem) => {
    if (openingBusinessId) return;
    viewerOrderRef.current = orderedItems.map((candidate) => candidate.businessId);
    if (isStoryBusinessGroup(item)) {
      const group = filterPublicStoryGroups([item])[0] || null;
      if (group) setViewerGroups([group]);
      return;
    }
    setOpeningBusinessId(item.businessId);
    const group = await loadSequence(item).catch(() => null);
    setOpeningBusinessId(null);
    if (!group) {
      const remaining = itemsRef.current.filter((candidate) => candidate.businessId !== item.businessId);
      itemsRef.current = remaining;
      setItems(remaining);
      return;
    }
    setViewerGroups([group]);
  }, [loadSequence, openingBusinessId, orderedItems]);

  const loadNextViewerGroup = useCallback(async (currentBusinessId: string) => {
    let orderedIds = viewerOrderRef.current;
    let currentIndex = orderedIds.indexOf(currentBusinessId);
    if (currentIndex < 0) return null;
    if (currentIndex === orderedIds.length - 1 && hasMoreRef.current) {
      const merged = await loadMore();
      const appendedIds = merged
        .map((item) => item.businessId)
        .filter((businessId) => !orderedIds.includes(businessId));
      orderedIds = [...orderedIds, ...appendedIds];
      viewerOrderRef.current = orderedIds;
      currentIndex = orderedIds.indexOf(currentBusinessId);
    }
    for (const businessId of orderedIds.slice(currentIndex + 1)) {
      const candidate = itemsRef.current.find((item) => item.businessId === businessId);
      if (!candidate) continue;
      const group = await loadSequence(candidate).catch(() => null);
      if (group) return group;
    }
    return null;
  }, [loadMore, loadSequence]);

  const markBusinessSequenceSeen = useCallback((group: StoryBusinessGroup) => {
    const item = itemsRef.current.find((candidate) => candidate.businessId === group.businessId) || group;
    const newestStoryAt = getStoryBusinessNewestAt(item);
    if (!newestStoryAt) return;
    setSeenBusinesses((current) => {
      const next = { ...current, [group.businessId]: newestStoryAt };
      try {
        window.localStorage.setItem(SEEN_BUSINESS_STORAGE_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  }, []);

  if (!orderedItems.length) return null;

  return (
    <>
      <section
        aria-labelledby="business-stories-heading"
        className={compact ? "mt-4" : "w-full bg-[#f8f7fb] py-4"}
        data-testid="business-stories-row"
      >
        <div className={compact ? "" : "mx-auto w-full max-w-[1180px] px-4 sm:px-6 md:px-8"}>
          <h2
            id="business-stories-heading"
            className="text-lg font-semibold tracking-[-0.025em] text-slate-950 sm:text-xl"
          >
            {compact ? "Stories" : "Happening nearby"}
          </h2>
          <div
            ref={trayRef}
            onScroll={() => setPaginationArmed(true)}
            className="mt-2.5 flex flex-nowrap items-start justify-start gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {orderedItems.map((item) => {
              const unseen = !isStoryBusinessSeen(item, seenBusinesses);
              const opening = openingBusinessId === item.businessId;
              return (
                <button
                  key={item.businessId}
                  type="button"
                  onClick={() => void openStoryBusiness(item)}
                  disabled={opening}
                  className="group flex w-[72px] shrink-0 flex-col items-center gap-1.5 rounded-xl text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 disabled:opacity-60"
                  aria-label={`View ${item.businessName} stories${unseen ? ", new stories available" : ""}`}
                >
                  <StoryRing unseen={unseen}>
                    {item.avatarUrl ? (
                      <img
                        src={item.avatarUrl}
                        alt=""
                        width={58}
                        height={58}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      initials(item.businessName)
                    )}
                  </StoryRing>
                  <span className="line-clamp-2 text-xs font-medium leading-4 text-slate-700 group-hover:text-[#4c1d95]">
                    {item.businessName}
                  </span>
                </button>
              );
            })}
            {loadingMore ? Array.from({ length: 2 }).map((_, index) => (
              <span key={index} aria-hidden="true" className="flex w-[72px] shrink-0 flex-col items-center gap-1.5">
                <span className="h-[66px] w-[66px] animate-pulse rounded-full bg-violet-100" />
                <span className="h-3 w-14 animate-pulse rounded bg-slate-200" />
              </span>
            )) : null}
            {loadError ? (
              <button
                type="button"
                onClick={() => void loadMore()}
                className="min-h-11 shrink-0 rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700"
              >
                Retry
              </button>
            ) : null}
            {progressive && moreAvailable ? (
              <div ref={sentinelRef} className="h-1 w-px shrink-0" aria-hidden="true" />
            ) : null}
          </div>
        </div>
      </section>

      {viewerGroups ? (
        <StoryViewer
          groups={viewerGroups}
          mode={mode}
          onGroupComplete={markBusinessSequenceSeen}
          loadNextGroup={loadNextViewerGroup}
          onClose={() => setViewerGroups(null)}
        />
      ) : null}
    </>
  );
}
